import { useCallback, useEffect, useRef, useState } from 'react';

import { ConversationsV2ChatState } from '@spotify/confidence-graphql';

import {
  type ChatUpdatedOnDataParams,
  writeChatToCache,
} from './writeChatToCache';

const STALENESS_TIMEOUT_MS = 30_000;

export const useChatSubscriptionState = (chatName: string) => {
  const [state, setState] = useState<ConversationsV2ChatState | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const trackedNameRef = useRef(chatName);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  if (trackedNameRef.current !== chatName) {
    trackedNameRef.current = chatName;
    setState(null);
    setDisplayName(null);
    clearTimeout(timerRef.current);
  }

  const onSubscriptionData = useCallback((params: ChatUpdatedOnDataParams) => {
    writeChatToCache(params);
    const chat = params.data.data?.chatUpdated?.chat;
    if (chat && chat.name === trackedNameRef.current) {
      if (chat.state) {
        setState(chat.state);
        clearTimeout(timerRef.current);
        if (chat.state === ConversationsV2ChatState.Running) {
          timerRef.current = setTimeout(
            () => setState(null),
            STALENESS_TIMEOUT_MS,
          );
        }
      }
      if (typeof chat.displayName === 'string') {
        setDisplayName(chat.displayName);
      }
    }
  }, []);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return {
    subscriptionState: state,
    subscriptionDisplayName: displayName,
    onSubscriptionData,
  };
};
